import { Observable, forkJoin, from, map, switchMap } from "rxjs";

import { ARTEFAKT_BASIS } from "../../config/app-config.js";

/**
 * Artefakte — Dateien, die die Pipeline je Lauf ganz schreibt und die die
 * App per schlichter URL holt (CLAUDE.md: Artefakte per URL).
 *
 * KEIN Firestore, KEIN Provider: Ein Artefakt ist für alle identisch,
 * unveränderlich je Lauf und liegt im öffentlichen Bucket wie die
 * Geometrien. `fetch` genügt.
 *
 * Das MUSTER „Manifest + eine Datei je Jahr" teilen Drucksachen- und
 * Sitzungen-Liste; deshalb liegt es hier, nicht in einem Modul.
 */
export async function holeArtefakt<T>(datei: string): Promise<T> {
  const antwort = await fetch(`${ARTEFAKT_BASIS}/${datei}`, {
    headers: { Accept: "application/json" },
  });
  if (!antwort.ok) {
    throw new Error(`Artefakt ${datei} nicht erreichbar (${antwort.status})`);
  }
  return (await antwort.json()) as T;
}

/** Ein Manifest, das auf Jahresdateien zeigt. */
export interface JahresManifest {
  jahre: Array<{ jahr: number; anzahl: number; datei: string; hash?: string }>;
}

/**
 * Der Cache-Schlüssel einer Jahresdatei.
 *
 * DAS PROBLEM, das er löst: Die Jahresdateien liegen unter festen Namen im
 * Bucket und werden überschrieben; vergangene Jahre sind einen Tag lang
 * gecacht, das Manifest nur fünf Minuten. Schreibt die Pipeline einen
 * geänderten Bestand, holt der Browser das laufende Jahr frisch und behält
 * die übrigen — die Liste zeigt dann einen GEMISCHTEN Stand, ohne dass es
 * auffällt. Genau so entstand am 29.08.2026 die Liste, die für Höhenhaus vier
 * statt elf Drucksachen zeigte, während Firestore daneben elf zählte.
 *
 * DIE LÖSUNG kommt aus dem Manifest: Es nennt je Jahresdatei die Prüfsumme
 * über ihren INHALT (`hash`), und die hängt hier an die URL. Eine geänderte
 * Datei bekommt damit eine neue Adresse, eine unveränderte behält ihre und
 * bleibt gecacht — die Aufteilung nach Jahren bleibt also wirksam, anders als
 * bei einem Zeitstempel je Lauf, der jedes Jahr neu lüde.
 *
 * Das Manifest ist dabei die frische Autorität, und die Pipeline lädt es
 * IMMER ZULETZT hoch: Zeigte ein neuer Index auf noch alte Dateien, legte der
 * Browser den alten Inhalt unter dem neuen Schlüssel ab — und der ändert sich
 * nie wieder (RUECKMELDUNG-cache-schluessel-listen.md).
 *
 * OHNE `hash` bleibt es beim reinen Dateinamen. Das Feld steht seit dem
 * 29.08.2026 in beiden Manifesten; der Rückfall kostet nichts und macht die
 * App unabhängig von der Reihenfolge der Auslieferungen.
 */
export function artefaktUrl(eintrag: { datei: string; hash?: string }): string {
  return eintrag.hash ? `${eintrag.datei}?v=${encodeURIComponent(eintrag.hash)}` : eintrag.datei;
}

/** Manifest zuerst, dann alle Jahresdateien parallel; Einträge in Manifest-Reihenfolge. */
export function ladeJahresListe<M extends JahresManifest, E>(
  manifestDatei: string,
): Observable<{ manifest: M; eintraege: E[] }> {
  return from(holeArtefakt<M>(manifestDatei)).pipe(
    switchMap((manifest) =>
      forkJoin(manifest.jahre.map((j) => from(holeArtefakt<{ items: E[] }>(artefaktUrl(j))))).pipe(
        map((jahre) => ({ manifest, eintraege: jahre.flatMap((j) => j.items) })),
      ),
    ),
  );
}
